import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// Components
import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';

// Utils
import { getProjectCode } from '../../utils/auth';
import { ProjectDetail } from '../../utils/commonUtils';
import { formatDateTimeFromApi } from '../../utils/dateTime';
import { getVoucherList, VoucherListRequest } from '../../utils/voucher';
import { VoucherFormUtils } from '../../utils/voucherCommon';
import { getBrandListFlat, FlatBrandNode } from '../../utils/brand';
import { 
  Search,
  Filter,
  Plus,
  Edit,
  Trash2,
  Eye,
  Download,
  MoreVertical,
  DollarSign,
  Percent,
  Tag,
  Calendar,
  Clock,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Ticket,
  ShoppingBag,
  Building,
  Zap,
  Users2,
  Infinity,
  Ban,
  CheckCircle,
  XCircle,
  Grid,
  Grid3X3,
  List,
  Map,
  CreditCard,
  ChevronRight,
  ChevronLeft,
  Coffee,
  Gift,
  Settings,
  ChevronDown,
  ChevronUp,
  Package,
  Store,
  MessageSquare,
  BarChart3,
  Activity,
  ArrowUpDown,
  Home
} from 'lucide-react';

const VoucherList: React.FC = () => {
  // Router hooks
  const navigate = useNavigate();
  const location = useLocation();

  // Get project code
  const projectCode = getProjectCode();

  // UI state
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('voucher-list');

  // List state
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  // URL 파라미터에서 페이징 정보 읽기
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const page = searchParams.get('page');
    const size = searchParams.get('size');
    
    if (page !== null) {
      setCurrentPage(parseInt(page, 10));
    }
    if (size !== null) {
      setPageSize(parseInt(size, 10));
    }
  }, [location.search]);

  // URL 파라미터에서 뷰 타입 읽기
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const view = urlParams.get('view');
    if (view && ['list', 'grid'].includes(view)) {
      setViewType(view);
    }
  }, [location.search]);

  const [sortField, setSortField] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [selectedVouchers, setSelectedVouchers] = useState<string[]>([]);
  const [viewType, setViewType] = useState('list');
  const [hoveredVoucher, setHoveredVoucher] = useState<string | null>(null);

  // API state
  const [vouchers, setVouchers] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Project state
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);

  // Brand state
  const [brands, setBrands] = useState<FlatBrandNode[]>([]);

  // Check if screen is mobile size
  VoucherFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  // 프로젝트 정보 로드
  useEffect(() => {
    const projectInfo = VoucherFormUtils.loadProjectDetail();
    console.log('교환권 목록 - 프로젝트 정보 로드:', projectInfo);
    if (projectInfo) {
      setProjectDetail(projectInfo);
    }
  }, []);

  // 브랜드 목록 조회
  useEffect(() => {
    const fetchBrands = async () => {
      if (!projectDetail) return;
      try {
        const currentProjectCode = projectDetail.code || getProjectCode();
        if (!currentProjectCode) return;
        const response = await getBrandListFlat(currentProjectCode);
        if (response?.brands) setBrands(response.brands);
      } catch (err) {
        console.error('브랜드 목록 조회 오류:', err);
      }
    };
    if (projectDetail) fetchBrands();
  }, [projectDetail]);

  // 검색어 Debounce 처리 (500ms 지연, 최소 2글자 이상)
  useEffect(() => {
    const trimmedSearchTerm = searchTerm.trim();
    if (trimmedSearchTerm.length <= 1) {
      setDebouncedSearchTerm('');
      return;
    }
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(trimmedSearchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // 교환권목록 조회
  useEffect(() => {
    const fetchVouchers = async () => {
      if (!projectDetail) {
        console.log('프로젝트 정보가 없습니다.');
        return;
      }
      
      try {
        setIsLoading(true);
        setError(null);
        
        const currentProjectCode = projectDetail?.code || projectCode;
        if (!currentProjectCode) {
          throw new Error('프로젝트 코드가 없습니다.');
        }
        
        const request: VoucherListRequest = {
          page: currentPage,
          size: pageSize,
          sort: `${sortField},${sortOrder}`,
          status: selectedStatus !== 'all' ? selectedStatus : undefined,
          type: selectedType !== 'all' ? selectedType : undefined,
          search: debouncedSearchTerm || undefined,
          brandCode: selectedBrand !== 'all' ? selectedBrand : undefined
        };
        
        console.log('교환권목록 API 호출:', { currentProjectCode, request });
        const response = await getVoucherList(currentProjectCode, request);
        console.log('교환권목록 API 응답:', response);
        
        if (response && response.vouchers) {
          setVouchers(response.vouchers);
          setTotalCount(response.totalCount);
          // API 응답의 pageSize에 맞게 pageSize 업데이트
          if (response.pageSize && response.pageSize !== pageSize) {
            setPageSize(response.pageSize);
          }
          console.log('설정된 교환권 목록:', response.vouchers);
          console.log('설정된 총 개수:', response.totalCount);
          console.log('설정된 페이지 크기:', response.pageSize);
        } else {
          console.warn('응답에 vouchers 데이터가 없습니다:', response);
          setVouchers([]);
          setTotalCount(0);
        }
      } catch (err) {
        console.error('교환권목록 조회 오류:', err);
        setError(err instanceof Error ? err.message : '교환권목록을 불러오는 중 오류가 발생했습니다.');
      } finally {
        setIsLoading(false);
      }
    };

    if (projectDetail) {
      fetchVouchers();
    }
  }, [projectDetail, currentPage, pageSize, sortField, sortOrder, selectedStatus, selectedBrand, selectedType, debouncedSearchTerm]);

  // Helper functions
  const getVoucherTypeInfo = (type: string) => {
    const typeMap: { [key: string]: { label: string; color: string; icon: React.ReactElement } } = {
      'PREPAID': { label: '선불권', color: 'bg-blue-100 text-blue-800', icon: <CreditCard className="w-4 h-4" /> },
      'COUPON': { label: '쿠폰', color: 'bg-green-100 text-green-800', icon: <Ticket className="w-4 h-4" /> },
      'BIZ': { label: '기업용', color: 'bg-purple-100 text-purple-800', icon: <Building className="w-4 h-4" /> },
      'EVENT': { label: '이벤트', color: 'bg-pink-100 text-pink-800', icon: <Zap className="w-4 h-4" /> }
    };
    return typeMap[type] || { label: type, color: 'bg-gray-100 text-gray-800', icon: <Tag className="w-4 h-4" /> };
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <span className="w-1.5 h-1.5 mr-1 bg-green-400 rounded-full"></span>
            활성
          </span>
        );
      case 'INACTIVE':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <span className="w-1.5 h-1.5 mr-1 bg-gray-400 rounded-full"></span>
            비활성
          </span>
        );
      default:
        return null;
    }
  };

  const getDiscountTypeLabel = (type: string) => {
    const typeMap: { [key: string]: string } = {
      'AMOUNT': '정액',
      'PERCENTAGE': '정률',
      'FIXED': '고정'
    };
    return typeMap[type] || type;
  };

  // Sort function
  const handleSort = (field: string) => {
    VoucherFormUtils.handleSort(field, sortField, sortOrder, setSortField, setSortOrder);
  };

  // 페이지 변경 핸들러
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // 페이지 크기 변경 핸들러
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(0);
  };

  // 검색 조건 변경 시 페이지 리셋
  useEffect(() => {
    if (projectDetail) {
      setCurrentPage(0);
    }
  }, [debouncedSearchTerm, selectedStatus, selectedBrand, selectedType]);

  const getDiscountTargetLabel = (target: string) => {
    const targetMap: { [key: string]: string } = {
      'PRODUCT': '상품',
      'PAYMENT': '결제',
      'REWARD': '리워드'
    };
    return targetMap[target] || target;
  };

  const formatPrice = (price: number) => {
    return price ? price.toLocaleString('ko-KR') + '원' : '0원';
  };

  const formatValidPeriod = (voucher: any) => {
    if (voucher.validType === 0) {
      return voucher.validPeriod ? `${voucher.validPeriod}일` : '무제한';
    } else {
      return voucher.validUntil
        ? formatDateTimeFromApi(voucher.validUntil) || voucher.validUntil
        : '미지정';
    }
  };

  // 서버 사이드 필터링을 사용하므로 클라이언트 필터링 제거
  const filteredVouchers = vouchers;

  const renderEmptyVoucherList = () => (
    <div className="flex flex-col items-center py-12">
      <Ticket className="w-12 h-12 text-gray-400 mb-4" />
      <h3 className="text-lg font-medium text-gray-900 mb-2">교환권이 없습니다</h3>
      <p className="text-gray-500 mb-4">새로운 교환권을 생성해보세요</p>
      <button
        onClick={() => VoucherFormUtils.handleVoucherCreate(navigate)}
        className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
      >
        <Plus className="w-4 h-4" />
        교환권 생성
      </button>
    </div>
  );

  // 프로젝트 정보가 없을 때 처리
  if (!projectDetail) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center">
          {error ? (
            <>
              <div className="text-red-500 mb-4">
                <Home className="w-12 h-12 mx-auto" />
              </div>
              <p className="text-red-600 mb-4 text-center max-w-md">{error}</p>
              <button
                onClick={() => {
                  setError(null);
                  window.location.reload();
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                새로고침
              </button>
            </>
          ) : (
            <>
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-gray-600">프로젝트 정보를 불러오는 중...</p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      {/* Sidebar */}
      <ProjectSidebar
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        projectDetail={projectDetail}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Bar */}
        <Topbar
          title="교환권 관리"
          subtitle={projectDetail?.name || '프로젝트를 선택해주세요'}
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
          showMobileLogo={true}
          projectDetail={projectDetail}
        />

        {/* List Content */}
        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-full">
            {/* Filters and Actions */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
                <div className="flex flex-col sm:flex-row gap-3 flex-1 w-full">
                  {/* Search */}
                  <div className="relative flex-1 sm:max-w-xs min-w-[120px]">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="교환권명, 코드로 검색"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Status Filter */}
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">전체 상태</option>
                    <option value="ACTIVE">활성</option>
                    <option value="INACTIVE">비활성</option>
                  </select>

                  {/* Brand Filter */}
                  <select
                    value={selectedBrand}
                    onChange={(e) => setSelectedBrand(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">전체 브랜드</option>
                    {brands.map((brand) => (
                      <option key={brand.code} value={brand.code}>
                        {brand.name}
                      </option>
                    ))}
                  </select>

                  {/* Type Filter */}
                  <select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">전체 타입</option>
                    <option value="PREPAID">선불권</option>
                    <option value="COUPON">쿠폰</option>
                    <option value="BIZ">기업용</option>
                    <option value="EVENT">이벤트</option>
                  </select>

                  {/* View Type Toggle */}
                  <div className="flex items-center bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => {
                        setViewType('list');
                        const urlParams = new URLSearchParams(location.search);
                        urlParams.set('view', 'list');
                        navigate(`/vouchers?${urlParams.toString()}`, { replace: true });
                      }}
                      className={`p-1.5 rounded ${viewType === 'list' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}
                      title="목록 보기"
                    >
                      <List className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        setViewType('grid');
                        const urlParams = new URLSearchParams(location.search);
                        urlParams.set('view', 'grid');
                        navigate(`/vouchers?${urlParams.toString()}`, { replace: true });
                      }}
                      className={`p-1.5 rounded ${viewType === 'grid' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}
                      title="그리드 보기"
                    >
                      <Grid3X3 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="flex gap-2 w-full sm:w-auto">
                  <button
                    onClick={() => alert('엑셀 다운로드 기능은 준비중입니다.')}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    엑셀
                  </button>
                  <button
                    onClick={() => VoucherFormUtils.handleVoucherCreate(navigate)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    교환권 생성
                  </button>
                </div>
              </div>

              {/* Summary */}
              <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  검색결과 <span className="font-medium text-gray-900">{totalCount}</span>개 교환권
                </p>
                <div className="flex items-center gap-4 text-sm">
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-1"></span>
                    활성: {vouchers.filter((v) => v.status === 'ACTIVE').length}
                  </span>
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-gray-400 rounded-full mr-1"></span>
                    비활성: {vouchers.filter((v) => v.status === 'INACTIVE').length}
                  </span>
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mr-1"></span>
                    선불권: {vouchers.filter((v) => v.type === 'PREPAID').length}
                  </span>
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-1"></span>
                    쿠폰: {vouchers.filter((v) => v.type === 'COUPON').length}
                  </span>
                </div>
              </div>
            </div>

            {/* Error State */}
          {error && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="text-red-500 mb-4">
                <Home className="w-12 h-12 mx-auto" />
              </div>
              <p className="text-red-600 mb-4 text-center max-w-md mx-auto">{error}</p>
              <button
                onClick={() => {
                  setError(null);
                  window.location.reload();
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                다시 시도
              </button>
            </div>
          )}

          {/* Voucher List - 에러가 없을 때만 표시 */}
          {!error && (
            viewType === 'grid' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {isLoading ? (
                  <div className="col-span-full flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                  </div>
                ) : filteredVouchers.length === 0 ? (
                  <div className="col-span-full">
                    {renderEmptyVoucherList()}
                  </div>
                ) : (
                  filteredVouchers.map((voucher) => {
                    const typeInfo = getVoucherTypeInfo(voucher.type);
                    return (
                      <div
                        key={voucher.code}
                        className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow relative"
                        onMouseEnter={() => setHoveredVoucher(voucher.code)}
                        onMouseLeave={() => setHoveredVoucher(null)}
                      >
                        {/* Voucher Preview Card */}
                        <div className="p-4">
                          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                            {/* Type and Status */}
                            <div className="flex items-center justify-center mb-2">
                              <div className="w-4 h-4 mr-2">
                                {typeInfo.icon}
                              </div>
                              <span className="text-sm font-medium text-gray-700">
                                {typeInfo.label}
                              </span>
                              <div className="ml-2">
                                {getStatusBadge(voucher.status)}
                              </div>
                            </div>
                            
                            {/* Voucher Name */}
                            <h4 className="font-semibold text-gray-900 mb-2 text-sm">
                              {voucher.name}
                            </h4>
                            
                            {/* Comment */}
                            {voucher.comment && (
                              <p className="text-xs text-gray-600 mb-2 line-clamp-2">
                                {voucher.comment}
                              </p>
                            )}
                            
                            {/* Price/Discount */}
                            <div className="text-lg font-bold text-blue-600 mb-2">
                              {voucher.type === 'PREPAID' || voucher.type === 'BIZ' ? (
                                `${formatPrice(voucher.price)}원`
                              ) : (
                                voucher.discountType === 'PERCENTAGE' 
                                  ? `${voucher.discountValue}% 할인` 
                                  : `${formatPrice(voucher.discountValue || 0)}원 할인`
                              )}
                            </div>
                            
                            {/* Code */}
                            <p className="text-xs text-gray-500">
                              {voucher.masterCode}
                            </p>
                          </div>
                        </div>

                        {/* Hover Actions */}
                        {hoveredVoucher === voucher.code && (
                          <div className="absolute inset-x-0 bottom-0 bg-white border-t border-gray-200 p-3 flex justify-around">
                            <button className="flex items-center text-sm text-gray-600 hover:text-blue-600">
                              <Eye className="w-4 h-4 mr-1" />
                              상세
                            </button>
                            <button 
                              className="flex items-center text-sm text-gray-600 hover:text-blue-600"
                              onClick={() => VoucherFormUtils.handleVoucherEdit(navigate, voucher.code, currentPage, pageSize)}
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              수정
                            </button>
                            <button 
                              className="flex items-center text-sm text-gray-600 hover:text-red-600"
                              onClick={() => {
                                if (confirm(`${voucher.name}을(를) 삭제하시겠습니까?`)) {
                                  alert('삭제 기능은 준비 중입니다.');
                                }
                              }}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              삭제
                            </button>
                          </div>
                        )}
                    </div>
                  );
                  })
                )}
              </div>
            ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200" style={{ width: '100%', minWidth: '1320px' }}>
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="pl-6 pr-0 py-3 text-left" style={{ minWidth: '100px' }}>
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                          브랜드
                        </span>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '280px' }}>
                        <button
                          onClick={() => handleSort('name')}
                          className="group inline-flex items-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                        >
                          교환권 정보
                          <ArrowUpDown className="ml-1 h-3 w-3" />
                        </button>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '180px' }}>
                        <button
                          onClick={() => handleSort('price')}
                          className="group inline-flex items-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                        >
                          가격/할인
                          <ArrowUpDown className="ml-1 h-3 w-3" />
                        </button>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '160px' }}>
                        <button
                          onClick={() => handleSort('validUntil')}
                          className="group inline-flex items-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                        >
                          유효기간
                          <ArrowUpDown className="ml-1 h-3 w-3" />
                        </button>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '180px' }}>
                        <button
                          onClick={() => handleSort('issueLimit')}
                          className="group inline-flex items-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                        >
                          통계
                          <ArrowUpDown className="ml-1 h-3 w-3" />
                        </button>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '110px' }}>
                        <button
                          onClick={() => handleSort('status')}
                          className="group inline-flex items-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                        >
                          상태
                          <ArrowUpDown className="ml-1 h-3 w-3" />
                        </button>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '120px' }}>
                        액션
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {isLoading ? (
                      <tr>
                        <td colSpan={8} className="px-6 py-12 text-center">
                          <div className="flex justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                          </div>
                        </td>
                      </tr>
                    ) : filteredVouchers.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-6 py-12 text-center">
                          {renderEmptyVoucherList()}
                        </td>
                      </tr>
                    ) : (
                    filteredVouchers.map((voucher) => {
                      const typeInfo = getVoucherTypeInfo(voucher.type);
                      const brandCode = (voucher as { brandCode?: string }).brandCode;
                      const brandName = (voucher as { brandName?: string }).brandName;
                      return (
                        <tr key={voucher.code} className="hover:bg-gray-50">
                          <td className="pl-6 pr-0 py-4 whitespace-nowrap text-sm text-gray-600" style={{ minWidth: '100px' }}>
                            {brandCode
                              ? (brands.find(b => b.code === brandCode)?.name ?? brandName ?? brandCode)
                              : (brandName ?? '-')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0">
                                <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium ${typeInfo.color}`}>
                                  <div className="w-3 h-3 mr-1">
                                    {typeInfo.icon}
                                  </div>
                                  {typeInfo.label}
                                </span>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">{voucher.name}</div>
                                <div className="text-sm text-gray-500">{voucher.masterCode}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {voucher.type === 'PREPAID' || voucher.type === 'BIZ' ? (
                              <div>
                                <div className="text-sm text-gray-900">판매: {formatPrice(voucher.price)}</div>
                                <div className="text-sm text-blue-600">잔액: {formatPrice(voucher.balance)}</div>
                              </div>
                            ) : (
                              <div>
                                <div className="text-sm text-gray-900">
                                  {voucher.discountType === 'PERCENTAGE' 
                                    ? `${voucher.discountValue}%` 
                                    : formatPrice(voucher.discountValue || 0)
                                  }
                                </div>
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatValidPeriod(voucher)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              발행제한: {voucher.issueLimit || 0} | 유효기간: {voucher.validPeriod || 0}일
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(voucher.status)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <button 
                                onClick={() => alert(`${voucher.name} 상세보기 기능은 준비 중입니다.`)}
                                className="text-gray-600 hover:text-gray-900"
                                title="상세보기"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button 
                                onClick={() => VoucherFormUtils.handleVoucherEdit(navigate, voucher.code, currentPage, pageSize)}
                                className="text-gray-600 hover:text-gray-900"
                                title="수정"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button 
                                onClick={() => {
                                  if (confirm(`${voucher.name}을(를) 삭제하시겠습니까?`)) {
                                    alert('삭제 기능은 준비 중입니다.');
                                  }
                                }}
                                className="text-gray-600 hover:text-gray-900"
                                title="삭제"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            )
          )}

          {/* Pagination */}
          {vouchers.length > 0 && (
            <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">페이지당</span>
                  <select 
                    value={pageSize}
                    onChange={(e) => {
                      handlePageSizeChange(parseInt(e.target.value));
                    }}
                    className="px-1 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="10">10개</option>
                    <option value="20">20개</option>
                    <option value="50">50개</option>
                    <option value="100">100개</option>
                  </select>
                  <span>표시</span>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handlePageChange(0)}
                    disabled={currentPage === 0}
                    className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    처음
                  </button>
                  <button
                    onClick={() => handlePageChange(Math.max(0, currentPage - 1))}
                    disabled={currentPage === 0}
                    className="px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  
                  {/* Page Numbers */}
                  {Array.from({ length: Math.ceil(totalCount / pageSize) }, (_, i) => i).slice(0, 5).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-1 rounded text-sm ${
                        currentPage === page
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {page + 1}
                    </button>
                  ))}
                  
                  <button
                    onClick={() => handlePageChange(Math.min(Math.ceil(totalCount / pageSize) - 1, currentPage + 1))}
                    disabled={currentPage >= Math.ceil(totalCount / pageSize) - 1}
                    className="px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handlePageChange(Math.ceil(totalCount / pageSize) - 1)}
                    disabled={currentPage >= Math.ceil(totalCount / pageSize) - 1}
                    className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    마지막
                  </button>
                </div>

                <p className="text-sm text-gray-600">
                  {currentPage * pageSize + 1} - {Math.min((currentPage + 1) * pageSize, totalCount)} / 총 {totalCount}개
                </p>
              </div>
            </div>
          )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default VoucherList;